import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import Sidebar from "../../global/Sidebar";
import NoData from "../ErrorMessage";
import { FiltersContext } from "../../store/filters-options-context";
import { CCAsections, countries } from "../../data/mockData";
import {
    factsheetExists,
    factsheetFileUrl,
    requestFactsheet,
    iso3ToIso2,
    iso3ToLabel,
} from "../../data/factsheets";

// Shareable deep link, e.g. /#/factsheet/CHL. It doesn't render the factsheet itself: it
// resolves the country and hands over to the normal indicators page, asking it to open the
// factsheet dialog. Rendering here instead would strand the visitor - the context derives
// its state by parsing the path (getRoutePathInfo), so on an unknown route shape no country
// is hydrated, mainCategory stays empty, and every sidebar selection silently does nothing.
export default function FactsheetPage() {
    const { iso3: iso3Param } = useParams();
    const navigate = useNavigate();
    const { params } = useContext(FiltersContext);

    const iso3 = (iso3Param || "").toUpperCase();
    const label = iso3ToLabel[iso3];
    const country = countries.find(
        (item) => item.code?.toUpperCase() === iso3ToIso2[iso3]
    );
    const url = country ? factsheetFileUrl(iso3) : null;

    // null while the check is in flight, so a missing factsheet doesn't flash an error
    // before we know whether the file is there.
    const [available, setAvailable] = useState(null);

    useEffect(() => {
        setAvailable(null);
        if (!url) {
            setAvailable(false);
            return;
        }

        const controller = new AbortController();
        let active = true;
        factsheetExists(url, controller.signal)
            .then((exists) => active && setAvailable(exists))
            .catch(() => active && setAvailable(false));

        return () => {
            active = false;
            controller.abort();
        };
    }, [url]);

    useEffect(() => {
        if (available !== true || !country) return;

        // First option of each of the other sidebar selectors, which is what the app itself
        // falls back to when a route has to be built without an explicit choice.
        const section = CCAsections[0]?.value?.toLowerCase();
        const subCat = CCAsections[0]?.indicators?.[0]?.slug;
        const years = params?.year?.join(",");
        if (!section || !subCat || !years) return;

        const path = `/indicators/${section}/${subCat}/${encodeURIComponent(
            country.id
        )}/${encodeURIComponent(years)}`;

        // Not router state: the indicators page re-navigates a couple of times while it
        // settles, and each of those calls would drop it.
        requestFactsheet(iso3);
        // replace: the factsheet URL should not sit in the history between the page and
        // wherever the visitor came from, or Back would bounce straight through it again.
        navigate(path, { replace: true });
    }, [available, country, iso3, params, navigate]);

    if (available === null || available === true) return null;

    return (
        <Box display="flex" flexDirection="row" className="container">
            <Sidebar />
            <Box display="flex" flexDirection="column" className="right-column">
                <Box sx={{ m: 3 }}>
                    <NoData
                        severity="warning"
                        text={
                            label
                                ? `No factsheet is available for ${label} (${iso3}).`
                                : `"${iso3Param}" is not a known ISO3 country code.`
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
}
